/**
 * LevNutri - Gerenciamento de Nutricionistas (Admin only)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { usersService } from '../../services/api';
import { Ico, emo } from '../ui/Ico';

const C = {
  primary:   '#D4A574',
  accent:    '#e8c9a8',
  bg:        '#fbf8f5',
  surface:   '#FFFFFF',
  dark:      '#2f2a26',
  gray600:   '#736b64',
  gray200:   '#ebe3da',
  success:   '#46714f',
  error:     '#c46a60',
  warning:   '#c4933f',
  admin:     '#7189a6',
};

const S = {
  container: {
    padding: '2rem',
    maxWidth: '960px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '600',
    color: C.dark,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.9rem',
    color: C.gray600,
    marginTop: '0.25rem',
  },
  btn: (bg = C.primary, color = '#ffffff') => ({
    padding: '0.6rem 1.25rem',
    background: bg,
    color,
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    transition: 'opacity .15s',
  }),
  card: {
    background: C.surface,
    borderRadius: '12px',
    border: `1px solid ${C.gray200}`,
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,.06)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  th: {
    padding: '0.9rem 1.25rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '600',
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: C.gray600,
    background: C.bg,
    borderBottom: `1px solid ${C.gray200}`,
  },
  td: (inactive) => ({
    padding: '0.9rem 1.25rem',
    borderBottom: `1px solid ${C.gray200}`,
    color: inactive ? C.gray600 : C.dark,
    opacity: inactive ? 0.7 : 1,
  }),
  badge: (color, bg) => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.72rem',
    fontWeight: '600',
    color,
    background: bg,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
  }),
  // Modal overlay
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    background: C.surface,
    borderRadius: '16px',
    padding: '2rem',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,.2)',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: C.dark,
    marginBottom: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: C.dark,
    marginBottom: '0.35rem',
  },
  input: {
    width: '100%',
    padding: '0.7rem 0.9rem',
    border: `1.5px solid ${C.gray200}`,
    borderRadius: '8px',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color .15s',
  },
  select: {
    width: '100%',
    padding: '0.7rem 0.9rem',
    border: `1.5px solid ${C.gray200}`,
    borderRadius: '8px',
    fontSize: '0.95rem',
    background: '#ffffff',
    outline: 'none',
    boxSizing: 'border-box',
  },
  errorBox: {
    background: '#f8ebe9',
    border: '1px solid #e3b3ad',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: C.error,
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  successBox: {
    background: '#edf4ee',
    border: '1px solid #b9d2bd',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: C.success,
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
};

const EMPTY_FORM = {
  name: '', name_display: '', email: '', crn: '', password: '', role: 'nutricionista',
};

function NutricionistaModal({ user, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState(
    isEdit
      ? { name: user.name, name_display: user.name_display || '', email: user.email, crn: user.crn || '', password: '', role: user.role }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setError('');
    if (!form.name || !form.email) { setError('Nome e e-mail são obrigatórios.'); return; }
    if (!isEdit && !form.password) { setError('Senha é obrigatória para novo cadastro.'); return; }

    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;

      let res;
      if (isEdit) {
        res = await usersService.update(user.id, payload);
      } else {
        res = await usersService.create(payload);
      }

      if (res.success) {
        onSaved(res.data, res.message);
      } else {
        setError(res.error || 'Erro ao salvar');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <h2 style={S.modalTitle}>{emo(isEdit ? '✏️ Editar Nutricionista' : '➕ Nova Nutricionista')}</h2>

        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.formGroup}>
          <label style={S.label}>Nome completo *</label>
          <input style={S.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Maria Santos" />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Nome de exibição</label>
          <input style={S.input} value={form.name_display} onChange={e => set('name_display', e.target.value)} placeholder="Como aparece no sistema" />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>E-mail *</label>
          <input style={S.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="maria@email.com" />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>CRN</label>
          <input style={S.input} value={form.crn} onChange={e => set('crn', e.target.value)} placeholder="Ex: CRN-4 12345" />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>{isEdit ? 'Nova senha (deixe em branco para manter)' : 'Senha temporária *'}</label>
          <input style={S.input} type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" />
        </div>
        <div style={{ ...S.formGroup, marginBottom: '1.5rem' }}>
          <label style={S.label}>Perfil de acesso</label>
          <select style={S.select} value={form.role} onChange={e => set('role', e.target.value)}>
            <option value="nutricionista">Nutricionista</option>
            <option value="admin">Administradora</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button style={S.btn('#f6f0ea', C.dark)} onClick={onClose} disabled={saving}>Cancelar</button>
          <button style={S.btn(C.primary)} onClick={handleSave} disabled={saving}>
            {emo(saving ? '⏳ Salvando...' : (isEdit ? '💾 Salvar' : '✅ Cadastrar'))}
          </button>
        </div>
      </div>
    </div>
  );
}

function Nutricionistas() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [modal, setModal]       = useState(null); // null | 'new' | user object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersService.getAll();
      if (res.success) setUsers(res.data || []);
      else setError(res.error || 'Erro ao carregar');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar nutricionistas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleSaved = (savedUser, msg) => {
    setModal(null);
    showSuccess(msg || 'Salvo com sucesso!');
    load();
  };

  const handleToggle = async (user) => {
    const newActive = !user.active;
    try {
      const res = await usersService.toggleActive(user.id, newActive ? 1 : 0);
      if (res.success) {
        showSuccess(res.message);
        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: newActive } : u));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar status');
    }
  };

  const activeCount   = users.filter(u => u.active).length;
  const inactiveCount = users.length - activeCount;

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}><Ico e="👥" sp />Nutricionistas</h1>
          <p style={S.subtitle}>
            {activeCount} ativa{activeCount !== 1 ? 's' : ''}
            {inactiveCount > 0 && ` · ${inactiveCount} inativa${inactiveCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button style={S.btn(C.primary)} onClick={() => setModal('new')}>
          <Ico e="➕" sp />Nova Nutricionista
        </button>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}
      {success && <div style={S.successBox}>{success}</div>}

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: C.gray600 }}>
            Carregando...
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: C.gray600 }}>
            Nenhuma nutricionista cadastrada.
          </div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nutricionista</th>
                <th style={S.th}>E-mail</th>
                <th style={S.th}>CRN</th>
                <th style={S.th}>Perfil</th>
                <th style={S.th}>Status</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const inactive = !u.active;
                const displayName = u.name_display || u.name;
                return (
                  <tr key={u.id}>
                    <td style={S.td(inactive)}>
                      <div style={{ fontWeight: '600' }}>{displayName}</div>
                      {u.name_display && u.name !== u.name_display && (
                        <div style={{ fontSize: '0.78rem', color: C.gray600 }}>{u.name}</div>
                      )}
                    </td>
                    <td style={S.td(inactive)}>{u.email}</td>
                    <td style={S.td(inactive)}>{u.crn || '—'}</td>
                    <td style={S.td(inactive)}>
                      {u.role === 'admin'
                        ? <span style={S.badge('#ffffff', C.admin)}>Admin</span>
                        : <span style={S.badge(C.primary, '#fbf8f5')}>Nutricionista</span>
                      }
                    </td>
                    <td style={S.td(inactive)}>
                      {u.active
                        ? <span style={S.badge(C.success, '#edf4ee')}>Ativa</span>
                        : <span style={S.badge(C.gray600, '#f6f0ea')}>Inativa</span>
                      }
                    </td>
                    <td style={{ ...S.td(inactive), textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
                        <button
                          style={S.btn('#f6f0ea', C.dark)}
                          title="Editar"
                          onClick={() => setModal(u)}
                        >
                          <Ico e="✏" />
                        </button>
                        <button
                          style={S.btn(u.active ? '#f8f1e2' : '#edf4ee', u.active ? '#8a5f1a' : '#2f5236')}
                          title={u.active ? 'Desativar acesso' : 'Ativar acesso'}
                          onClick={() => handleToggle(u)}
                        >
                          {emo(u.active ? '🔒 Desativar' : '🔓 Ativar')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <NutricionistaModal
          user={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

export default Nutricionistas;
